import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from '@sportson/core-web/utils/cloneDeep';

class VariantSelectorContainer extends Component {
    combinations = {};

    static propTypes = {
        callback: PropTypes.func.isRequired,
        productId: PropTypes.number.isRequired,
        render: PropTypes.func.isRequired,
        selectedVariant: PropTypes.object,
        variants: PropTypes.array.isRequired,
    };

    static defaultProps = {
        selectedVariant: null,
    };

    constructor(props) {
        super(props);
        this.state = this.setupState(this.props.variants);
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.productId !== nextProps.productId) {
            this.setState({ ...this.setupState(nextProps.variants) });
            return true;
        }

        if (JSON.stringify(this.props.variants) !== JSON.stringify(nextProps.variants)) {
            this.setState({ ...this.setupState(nextProps.variants) });
            return true;
        }

        if (
            this.props.selectedVariant &&
            JSON.stringify(this.props.selectedVariant) !== JSON.stringify(nextProps.selectedVariant) &&
            JSON.stringify(this.props.selectedVariant) ===
                JSON.stringify(this.getVariantByParametricSelection(nextState.selectedParametrics))
        ) {
            this.setState({ ...this.setupState(nextProps.variants) });
            return false;
        }
        return JSON.stringify(this.state) !== JSON.stringify(nextState);
    }

    setupState = (variants) => {
        if (variants?.length === 0) {
            return {};
        }

        const { selectedVariant } = this.props;
        const combinations = {};
        const selections = [];
        const selectedParametrics = [];

        if (variants?.length) {
            variants.forEach((variant, variantIndex) => {
                variant.variantParametrics.forEach((parametric, parametricIndex) => {
                    const variantValues = {
                        label: parametric.value,
                        value: parametric.value_2,
                        valueDescription: parametric.valueDescription,
                    };

                    if (variantIndex === 0) {
                        selections.push({
                            id: parametric.id,
                            name: parametric.name,
                            code: parametric.code,
                            values: [variantValues],
                        });

                        selectedParametrics.push({
                            id: parametric.id,
                            value: parametric.value_2,
                        });
                    } else {
                        let selectionMatch = false;
                        selections.forEach((selection) => {
                            if (selection.id === parametric.id) {
                                selectionMatch = selection;
                            }
                        });
                        if (selectionMatch && selectionMatch.values.indexOf(parametric.value_2) === -1) {
                            selectionMatch.values.push(variantValues);
                        }
                    }
                    if (selectedVariant && selectedVariant.id === variant.id) {
                        selectedParametrics[parametricIndex].value = parametric.value_2;
                    }
                });
                combinations[
                    variant.variantParametrics.map((parametric) => `${parametric.id}|${parametric.value_2}`).join('--')
                ] = variant;
            });
        }

        this.combinations = combinations;

        return {
            selections,
            selectedParametrics,
        };
    };

    static defaultState = {
        selections: [],
        selectedParametrics: [],
    };

    selectParametricValue = (parametricIndex, parametricValue) => {
        const { productId, callback } = this.props;
        const { selectedParametrics } = this.state;
        let isValidCombination = true;

        const newSelectedParametrics = cloneDeep(selectedParametrics);

        newSelectedParametrics.forEach((parametric, stateIndex) => {
            if (stateIndex === parametricIndex) {
                parametric.value = parametricValue;
            } else if (isValidCombination) {
                // This is just here to prevent it from running if a parent has no value
                const isCurrentIndexAValidCombination = this.checkPossibleSelection(
                    stateIndex,
                    parametric.id,
                    parametric.value,
                    newSelectedParametrics,
                );

                if (!isCurrentIndexAValidCombination) {
                    const validValue = this.findFirstValidValue(stateIndex, parametric.id, newSelectedParametrics);
                    parametric.value = validValue || false;

                    if (!validValue) {
                        isValidCombination = false;
                    }
                }
            } else {
                parametric.value = false;
            }
        });

        this.setState({ selectedParametrics: newSelectedParametrics }, () => {
            const variantByParametricCombination = this.getVariantByParametricSelection();
            callback(productId, variantByParametricCombination, newSelectedParametrics);
        });
    };

    findFirstValidValue = (parametricIndex, parametricId, selectedParametrics) => {
        const { selections } = this.state;

        // Get all possible values
        const parametricValues = selections[parametricIndex]?.values.map((val) => val.value) || [];

        // Find the first valid combination
        for (const value of parametricValues) {
            if (this.checkPossibleSelection(parametricIndex, parametricId, value, selectedParametrics)) {
                return value;
            }
        }

        return null;
    };

    getVariantByParametricSelection = (selectedParametricState = this.state.selectedParametrics) => {
        let variant = null;

        const combination = selectedParametricState
            .map((parametric) => `${parametric.id}|${parametric.value}`)
            .join('--');

        if (combination && this.combinations[combination]) {
            variant = this.combinations[combination];
        }

        return variant;
    };

    checkPossibleSelection = (
        parametricIndex,
        parametricId,
        parametricValue,
        selectedParametrics = this.state.selectedParametrics,
    ) => {
        const combinationPart = [];
        selectedParametrics.forEach((parametric, index) => {
            if (parametricIndex > index && parametric.value) {
                combinationPart.push(parametric);
            } else if (parametricIndex === index) {
                combinationPart.push({ id: parametricId, value: parametricValue });
            }
        });

        const validCombinations = Object.keys(this.combinations).filter((combination) => {
            const splitCombination = combination.split('--');
            return combinationPart.every(({ id, value }) =>
                splitCombination.some((singleCombination) => singleCombination === `${id}|${value}`),
            );
        });

        return validCombinations.length;
    };

    checkStockStatus = (
        parametricIndex,
        parametricId,
        parametricValue,
        selectedParametrics = this.state.selectedParametrics,
    ) => {
        const combinationPart = [];
        const currentSelections = this.state.selections;
        let currentIndexHasSelection = false;

        if (currentSelections[parametricIndex]?.selectedValue) {
            currentIndexHasSelection = true;
        }

        selectedParametrics.forEach((parametric, index) => {
            if (parametricIndex > index && parametric.value) {
                combinationPart.push(parametric);
            } else if (parametricIndex === index) {
                combinationPart.push({ id: parametricId, value: parametricValue });
            }
        });

        const validCombinations = Object.keys(this.combinations).filter((combination) => {
            const splitCombination = combination.split('--');
            return combinationPart.every(({ id, value }) =>
                splitCombination.some((singleCombination) => singleCombination === `${id}|${value}`),
            );
        });

        const validCombinationsWithStock = validCombinations.filter(
            (combination) =>
                (this.combinations[combination].onHand.value && this.combinations[combination].onHand.value > 0) ||
                (this.combinations[combination].onHandStock && this.combinations[combination].onHandStock.value > 0),
        );

        if (Object.values(selectedParametrics).length > 1) {
            const splitValues = validCombinationsWithStock.map((combination) =>
                combination.split('--').map((sub) => sub.split('|')[1]),
            );

            if (selectedParametrics.some((parametric) => parametric.value)) {
                const match = splitValues.every((value) =>
                    Object.values(selectedParametrics).every((parametric) => !value.includes(parametric.value)),
                ).length;
                if (match && !currentIndexHasSelection) {
                    return false;
                }
            }
        }

        return !!validCombinationsWithStock.length;
    };

    render() {
        const { render: Renderer, variants } = this.props;
        const { selectedParametrics, selections } = this.state;

        if (!variants?.length) {
            return null;
        }

        return Renderer ? (
            <Renderer
                checkPossibleSelection={this.checkPossibleSelection}
                checkStockStatus={this.checkStockStatus}
                getVariantByParametricSelection={this.getVariantByParametricSelection}
                selectedParametrics={selectedParametrics}
                selections={selections}
                selectParametricValue={this.selectParametricValue}
                variants={variants}
            />
        ) : null;
    }
}

export default VariantSelectorContainer;
